import { PlusIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { HolidayTable } from './components/HolidayTable';
import {
    ADD_LABEL,
    DUPLICATE_HINT,
    EMPTY_HINT,
    EMPTY_MESSAGE,
    LOADING_MESSAGE,
    READ_ONLY_HINT,
    SECTION_DESCRIPTION,
    SECTION_TITLE,
    YEAR_LABEL,
    buildDuplicateLabel,
    buildYearOptions,
} from './holidayManager.definitions';
import type { HolidayManagerProps } from './holidayManager.types';

export function HolidayManager({
    year,
    onYearChange,
    holidays,
    isLoading = false,
    canEdit = false,
    onCreate,
    onEdit,
    onRemove,
    onDuplicate,
    canDuplicate = false,
    isDuplicating = false,
}: HolidayManagerProps) {
    const yearOptions = buildYearOptions(new Date().getFullYear());

    return (
        <section className="space-y-4" data-tour="holidays-section">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-base font-semibold text-raro-gray-900 font-montserrat">
                        {SECTION_TITLE}
                    </h2>
                    <p className="mt-1 max-w-2xl text-sm text-raro-gray-600 font-montserrat">
                        {SECTION_DESCRIPTION}
                    </p>
                </div>

                {canEdit && (
                    <button
                        type="button"
                        onClick={onCreate}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 font-montserrat"
                        data-tour="holidays-add"
                    >
                        <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        {ADD_LABEL}
                    </button>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <label
                    htmlFor="holiday-year"
                    className="text-sm font-medium text-raro-gray-700 font-montserrat"
                >
                    {YEAR_LABEL}
                </label>
                <select
                    id="holiday-year"
                    value={year}
                    onChange={(event) => onYearChange(Number(event.target.value))}
                    className="rounded-xl border border-raro-gray-200 py-2 pl-3 pr-8 text-sm text-raro-gray-900 focus:border-primary-500 focus:ring-primary-500 font-montserrat"
                    data-tour="holidays-year"
                >
                    {yearOptions.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>

                {canEdit && (
                    <button
                        type="button"
                        onClick={onDuplicate}
                        disabled={isDuplicating || !canDuplicate}
                        title={DUPLICATE_HINT}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 px-3 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-raro-gray-200 disabled:text-raro-gray-400 disabled:hover:bg-transparent font-montserrat"
                        data-tour="holidays-duplicate"
                    >
                        <Square2StackIcon className="h-4 w-4" aria-hidden="true" />
                        {buildDuplicateLabel(year + 1)}
                    </button>
                )}

                {!canEdit && (
                    <span className="text-xs text-raro-gray-500 font-montserrat">
                        {READ_ONLY_HINT}
                    </span>
                )}
            </div>

            {isLoading ? (
                <p className="rounded-2xl border border-raro-gray-200 bg-white p-8 text-center text-sm text-raro-gray-500 font-montserrat">
                    {LOADING_MESSAGE}
                </p>
            ) : holidays.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-raro-gray-300 bg-white p-8 text-center">
                    <p className="text-sm text-raro-gray-700 font-montserrat">{EMPTY_MESSAGE}</p>
                    <p className="mx-auto mt-1 max-w-md text-xs text-raro-gray-500 font-montserrat">
                        {EMPTY_HINT}
                    </p>
                </div>
            ) : (
                <HolidayTable
                    holidays={holidays}
                    canEdit={canEdit}
                    onEdit={onEdit}
                    onRemove={onRemove}
                />
            )}

            {canEdit && holidays.length > 0 && (
                <p className="text-xs text-raro-gray-500 font-montserrat">{DUPLICATE_HINT}</p>
            )}
        </section>
    );
}

export default HolidayManager;
